import { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { useBrandSettings } from "@/hooks/useBrandSettings";
import { useAdminI18n } from "@/hooks/useAdminI18n";

function ImageUploader({
  label,
  value,
  onChange,
  placeholder,
  previewHeight = 120,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  placeholder: string;
  previewHeight?: number;
}) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith("image/")) {
        alert("Bitte laden Sie ein Bild hoch.");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        alert("Das Bild darf maximal 2 MB gross sein.");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        onChange(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    },
    [onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  return (
    <div>
      <label className="block text-sm font-semibold text-hj-dark mb-2">{label}</label>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
          dragOver ? "border-hj-red bg-hj-red/5" : "border-hj-gray-border hover:border-hj-gray"
        }`}
        onClick={() => inputRef.current?.click()}
      >
        {value ? (
          <div className="space-y-2">
            <div
              className="mx-auto rounded-md overflow-hidden bg-hj-gray-light"
              style={{ maxWidth: 200, height: previewHeight }}
            >
              <img
                src={value}
                alt={label}
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-xs text-hj-gray">Klicken oder Datei hierher ziehen, um zu ersetzen</p>
          </div>
        ) : (
          <div className="py-4">
            <i className="ri-upload-cloud-2-line text-3xl text-hj-gray mb-2"></i>
            <p className="text-sm text-hj-gray">{placeholder}</p>
            <p className="text-xs text-hj-gray mt-1">PNG, JPG, SVG (max. 2 MB)</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleInputChange}
          className="hidden"
        />
      </div>
      {value && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange("");
          }}
          className="mt-2 text-xs text-red-500 hover:text-red-600 transition-colors"
        >
          <i className="ri-delete-bin-line mr-1"></i>
          Entfernen
        </button>
      )}
    </div>
  );
}

function ColorPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (color: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-hj-dark mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <div className="relative w-12 h-12 rounded-lg overflow-hidden border border-hj-gray-border shrink-0">
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 w-[150%] h-[150%] -top-1/4 -left-1/4 cursor-pointer p-0 border-0"
          />
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-mono uppercase"
          placeholder="#000000"
        />
      </div>
    </div>
  );
}

export default function AdminBrandSettings() {
  const { settings, setSettings, resetSettings } = useBrandSettings();
  const { t } = useAdminI18n();
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-hj-gray mb-4">
          <Link to="/admin" className="hover:text-hj-red transition-colors">Dashboard</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-hj-dark">{t("brand.title")}</span>
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-hj-dark">Markeneinstellungen</h1>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-sm text-emerald-600 flex items-center gap-1">
                <i className="ri-check-line"></i>
                {t("brand.saved")}
              </span>
            )}
            <button
              onClick={resetSettings}
              className="px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors"
            >
              {t("brand.reset")}
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors"
            >
              {t("common.save")}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Text Settings */}
          <div className="bg-white rounded-lg border border-hj-gray-border p-5">
            <h2 className="text-lg font-bold text-hj-dark mb-4 flex items-center gap-2">
              <i className="ri-text text-hj-red"></i>
              {t("brand.texts")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-hj-dark mb-2">
                  {t("brand.siteName")} *
                </label>
                <input
                  type="text"
                  value={settings.siteName}
                  onChange={(e) => setSettings({ siteName: e.target.value })}
                  className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                  placeholder="Helvetik Journal"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-hj-dark mb-2">
                  {t("brand.slogan")}
                </label>
                <input
                  type="text"
                  value={settings.slogan}
                  onChange={(e) => setSettings({ slogan: e.target.value })}
                  className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                  placeholder="Die Schweiz im Fokus."
                />
              </div>
            </div>
          </div>

          {/* Logo Uploads */}
          <div className="bg-white rounded-lg border border-hj-gray-border p-5">
            <h2 className="text-lg font-bold text-hj-dark mb-4 flex items-center gap-2">
              <i className="ri-image-line text-hj-red"></i>
              {t("brand.logos")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <ImageUploader
                label={t("brand.logoStandard")}
                value={settings.logoUrl}
                onChange={(url) => setSettings({ logoUrl: url })}
                placeholder="Logo für helle Hintergründe hochladen"
                previewHeight={80}
              />
              <ImageUploader
                label={t("brand.logoDark")}
                value={settings.logoDarkUrl}
                onChange={(url) => setSettings({ logoDarkUrl: url })}
                placeholder="Logo für dunkle Hintergründe hochladen"
                previewHeight={80}
              />
              <ImageUploader
                label={t("brand.favicon")}
                value={settings.faviconUrl}
                onChange={(url) => setSettings({ faviconUrl: url })}
                placeholder="Favicon hochladen (32x32 oder 64x64)"
                previewHeight={64}
              />
            </div>
            <div className="mt-4 p-3 bg-hj-gray-light rounded-md">
              <p className="text-xs text-hj-gray">
                <i className="ri-information-line mr-1"></i>
                Wenn kein Logo hochgeladen wird, wird das integrierte SVG-Logo verwendet.
                Das dunkle Logo wird im Footer und auf dunklen Hintergründen angezeigt.
              </p>
            </div>
          </div>

          {/* Colors */}
          <div className="bg-white rounded-lg border border-hj-gray-border p-5">
            <h2 className="text-lg font-bold text-hj-dark mb-4 flex items-center gap-2">
              <i className="ri-palette-line text-hj-red"></i>
              {t("brand.colors")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <ColorPicker
                label={t("brand.primaryColor")}
                value={settings.primaryColor}
                onChange={(color) => setSettings({ primaryColor: color })}
              />
              <ColorPicker
                label={t("brand.accentColor")}
                value={settings.accentColor}
                onChange={(color) => setSettings({ accentColor: color })}
              />
            </div>
            <div className="mt-4 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded border border-hj-gray-border"
                  style={{ backgroundColor: settings.primaryColor }}
                ></div>
                <span className="text-xs text-hj-gray">Primär</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded border border-hj-gray-border"
                  style={{ backgroundColor: settings.accentColor }}
                ></div>
                <span className="text-xs text-hj-gray">Akzent</span>
              </div>
            </div>
          </div>

          {/* Live Preview */}
          <div className="bg-white rounded-lg border border-hj-gray-border p-5">
            <h2 className="text-lg font-bold text-hj-dark mb-4 flex items-center gap-2">
              <i className="ri-eye-line text-hj-red"></i>
              {t("brand.preview")}
            </h2>
            <div className="border border-hj-gray-border rounded-lg overflow-hidden">
              {/* Header preview */}
              <div className="bg-white px-4 py-3 border-b border-hj-gray-border">
                <div className="flex items-center gap-3">
                  {settings.logoUrl ? (
                    <img
                      src={settings.logoUrl}
                      alt={settings.siteName}
                      className="h-10 w-auto object-contain"
                    />
                  ) : (
                    <div className="relative w-10 h-10 shrink-0">
                      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
                        <rect
                          x="2" y="2" width="44" height="44" rx="8"
                          stroke={settings.accentColor} strokeWidth="2.5" fill="none"
                        />
                        <path d="M24 8 L24 20" stroke={settings.accentColor} strokeWidth="3" strokeLinecap="round" />
                        <path d="M24 28 L24 40" stroke={settings.accentColor} strokeWidth="3" strokeLinecap="round" />
                        <path d="M8 24 L20 24" stroke={settings.accentColor} strokeWidth="3" strokeLinecap="round" />
                        <path d="M28 24 L40 24" stroke={settings.accentColor} strokeWidth="3" strokeLinecap="round" />
                        <circle cx="24" cy="24" r="3" fill={settings.accentColor} />
                      </svg>
                    </div>
                  )}
                  <div>
                    <p className="font-heading text-lg font-bold" style={{ color: settings.primaryColor }}>
                      {settings.siteName || "Helvetik Journal"}
                    </p>
                    {settings.slogan && (
                      <p className="text-xs text-hj-muted">{settings.slogan}</p>
                    )}
                  </div>
                </div>
              </div>
              {/* Footer preview */}
              <div className="px-4 py-3" style={{ backgroundColor: settings.primaryColor }}>
                <div className="flex items-center gap-3">
                  {settings.logoDarkUrl ? (
                    <img
                      src={settings.logoDarkUrl}
                      alt={settings.siteName}
                      className="h-8 w-auto object-contain"
                    />
                  ) : settings.logoUrl ? (
                    <img
                      src={settings.logoUrl}
                      alt={settings.siteName}
                      className="h-8 w-auto object-contain"
                    />
                  ) : (
                    <div className="relative w-8 h-8 shrink-0">
                      <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
                        <rect
                          x="2" y="2" width="44" height="44" rx="8"
                          stroke={settings.accentColor} strokeWidth="2.5" fill="none"
                        />
                        <path d="M24 8 L24 20" stroke={settings.accentColor} strokeWidth="3" strokeLinecap="round" />
                        <path d="M24 28 L24 40" stroke={settings.accentColor} strokeWidth="3" strokeLinecap="round" />
                        <path d="M8 24 L20 24" stroke={settings.accentColor} strokeWidth="3" strokeLinecap="round" />
                        <path d="M28 24 L40 24" stroke={settings.accentColor} strokeWidth="3" strokeLinecap="round" />
                        <circle cx="24" cy="24" r="3" fill={settings.accentColor} />
                      </svg>
                    </div>
                  )}
                  <p className="text-sm font-bold text-white">{settings.siteName || "Helvetik Journal"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}